import {
  agendaRruleSchema,
  agendaSlotTypeSchema,
  CreateWorkScheduleSlotApiPayload,
  DayOfWeek,
  PeriodOfTheDay,
  PRESENTIEL_SLOT_TYPE,
  periodsOfTheDay,
} from '@omecare/utils';
import { UseMutationResult, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { AddOfficeWorkScheduleSlotFormValues } from '@/components/forms/add-office-work-schedule-slot-form/schema';
import http from '@/utils/http.service';
import { workScheduleSlotSequenceSchema } from '../queries/use-get-office-work-schedules-slots';

export type AddOfficeWorkScheduleSlotPayload = {
  officeId: string;
  startAt: {
    // Day format: YYYY-MM-DD
    date: string;
    tzOffset: number;
    between: {
      start: number;
      end: number;
    };
  };
  periodOfTheDay: PeriodOfTheDay;
  dayOfWeek: DayOfWeek;
} & Omit<AddOfficeWorkScheduleSlotFormValues, 'startAt'>;

export const addWorkScheduleSlotItemSchema = z.object({
  id: z.string().uuid(),
  startAt: z.coerce.date(),
  endAt: z.coerce.date(),
  periodOfTheDay: z.enum(periodsOfTheDay),
  doctorAttending: z.object({
    id: z.string().uuid(),
    firstname: z.string(),
    lastname: z.string(),
  }),
  rrule: agendaRruleSchema,
  exdate: z.array(z.string()),
  sourceId: z.string().nullable().default(null),
  sequence: workScheduleSlotSequenceSchema,
  occurenceIndex: z.number().optional(),
  slotType: agendaSlotTypeSchema.optional().default(PRESENTIEL_SLOT_TYPE),
  forceWithoutAssistant: z.coerce.boolean().optional().default(false),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type AddWorkScheduleSlotItemResponse = z.infer<
  typeof addWorkScheduleSlotItemSchema
>;

export const addWorkScheduleSlotResponseSchema = z.object({
  data: z.array(addWorkScheduleSlotItemSchema),
});
export type AddOfficeWorkScheduleSlotResponse = z.infer<
  typeof addWorkScheduleSlotResponseSchema
>;

const addOfficeWorkScheduleSlot = (
  payload: CreateWorkScheduleSlotApiPayload,
) => {
  return http.workScheduleSlots
    .create(payload)
    .then<AddOfficeWorkScheduleSlotResponse>(({ transform }) => {
      return transform.handle({
        attributes: {
          startAt: true,
          endAt: true,
          rrule: true,
          exdate: true,
          sourceId: true,
          occurenceIndex: true,
          slotType: true,
          withAssistante: true,
          forceWithoutAssistant: true,
          dayOfWeek: true,
          periodOfTheDay: true,
          createdAt: true,
          updatedAt: true,
        },
        relations: {
          doctorAttending: true,
          sequence: true,
        },
        schema: addWorkScheduleSlotResponseSchema,
      });
    });
};

const useAddOfficeWorkScheduleSlotMutation = (): UseMutationResult<
  AddOfficeWorkScheduleSlotResponse,
  Error,
  CreateWorkScheduleSlotApiPayload
> => {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: addOfficeWorkScheduleSlot,
    onSuccess() {
      toast.success(t('http.add-office-work-schedule-slot.success'));
    },
  });
};

export default useAddOfficeWorkScheduleSlotMutation;
